import type { Location, Scan, SealedProduct } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { idleMarketRefresh, type MarketRefreshStatus } from "../../core/primitives/catalog-market";
import type { Grade } from "../../core/primitives/grades";
import { idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { EditState } from "../../core/primitives/scan-edit";
import type { SealedEditState } from "../../core/primitives/sealed-edit";

/** An automatic re-price, until the merchant touches the price again. */
export interface RepriceNote {
  /** The figure that was overwritten, when there is one to put back. */
  readonly previous: string | null;
  readonly text: string;
}

export class ScanEditorState {
  /**
   * The scan as the server last described it. Editing is a round trip: a
   * correction can move the match, re-run the pricing lookup and change the
   * estimate, so this is what the screen reads, not the first fetch.
   */
  readonly scan = new Rx<Scan | null>(null);
  /** The form for a card. Replaced on every change, never mutated. Null for sealed product. */
  readonly edit = new Rx<EditState | null>(null);
  /** The form for sealed product. Null for a card. */
  readonly sealedEdit = new Rx<SealedEditState | null>(null);
  /** A draft save is in flight. */
  readonly saving = new Rx(false);
  /** A grade correction waits out the pause before it saves. */
  readonly draftPending = new Rx(false);
  /** A draft save that failed. Never blocks confirming. */
  readonly draftError = new Rx<string | null>(null);
  /** "Save and reprice" is on its way. */
  readonly savingDetails = new Rx(false);
  /** The card details changed since they were last priced: approving waits for "Save and reprice". */
  readonly needsReprice = new Rx(false);
  readonly repriceNote = new Rx<RepriceNote | null>(null);
  /** The candidate a pick is in flight for, by product id. */
  readonly picking = new Rx<string | null>(null);
  /** A search again for sealed product is in flight. */
  readonly searching = new Rx(false);
  /** A refresh of the shared market estimate the reviewer asked for. */
  readonly marketRefresh = new Rx<MarketRefreshStatus>(idleMarketRefresh);
  /** The reviewer typed the asking price; nothing the backend answers overwrites it. */
  readonly priceManual = new Rx(false);
  /** Derived: confirming would keep a product matched before the latest identity edits. */
  readonly staleMatchWarning = new Rx<string | null>(null);
  /** The suggested codes the reviewer approves as naming this item, by `codeKey`; each becomes the shop's vote. */
  readonly approvedCodes = new Rx<readonly string[]>([]);
  /** The catalogue product a pick is in flight for. */
  readonly catalogPicking = new Rx<string | null>(null);
  /** The catalogue moved while the scan was open; it was reloaded for another look. */
  readonly catalogNotice = new Rx<string | null>(null);
  /** A refused confirm or reject. */
  readonly error = new Rx<string | null>(null);
  /** A confirm or reject is in flight. */
  readonly busy = new Rx(false);
  /** Derived: pending, valid, priced for what it is, nothing in flight. */
  readonly canConfirm = new Rx(false);
  /** Derived: why a pending scan cannot be confirmed, when a field or a pick stops it. */
  readonly confirmBlocker = new Rx<string | null>(null);
  /** Off: the floor follows the asking price, as the quick row confirms. */
  readonly separateFloor = new Rx(false);
  /** Copies confirming stocks; zero confirms the card untracked. */
  readonly quantity = new Rx(1);
  readonly certNumber = new Rx("");
  /** What the shop paid for each copy, kept with the lot; blank for none. Sealed starts from what dealers pay. */
  readonly costPerCopy = new Rx("");
  /** Where the copies go. */
  readonly stockLocationId = new Rx<string | null>(null);
  /** The shop's branches, the counter the app works at first. */
  readonly locations = new RxList<Location>();
  /** From the store: how long an approval quotes the price. */
  readonly quoteValidityMinutes = new Rx<number | null>(null);
  /** The hand-made-item picker; shown only for a sealed scan that matched nothing. */
  readonly linkable = new Rx(false);
  readonly linkSearch = new Rx("");
  readonly linkStatus = new Rx<LoadStatus>(idleLoad);
  readonly linkCandidates = new RxList<SealedProduct>();
  /** False when the search found nothing and the list is every hand-made item. */
  readonly linkMatched = new Rx(true);
  /** Opt-ins: the linked item takes the scan's name, and asks the market price. */
  readonly linkAdoptName = new Rx(false);
  readonly linkAdoptPrice = new Rx(false);
  /** The hand-made item staff picked; confirming lands the scan on it. */
  readonly linkTarget = new Rx<SealedProduct | null>(null);
  /** Why the backend refused the picked item. */
  readonly linkConflict = new Rx<string | null>(null);
}

export abstract class ScanEditorActions {
  /** A field of the reading. */
  abstract change(updates: Partial<EditState>): void;
  /** The merchant's own price: no refetched estimate overwrites it after this. */
  abstract setPrice(price: string): void;
  abstract setFloor(floor: string): void;
  abstract setSeparateFloor(on: boolean): void;
  /** A grade from the list, which re-prices the card from that grade's comp. */
  abstract pickGrade(grade: Grade): void;
  /** A typed grade the reviewer has finished with, re-estimated the same way. */
  abstract commitGrade(grade: string): void;
  abstract setSlabbed(slabbed: boolean): void;
  abstract undoReprice(): void;
  abstract setQuantity(quantity: number): void;
  abstract setCertNumber(cert: string): void;
  abstract setStockLocation(locationId: string): void;
  /** Make one of the scan's candidates the pick; the price follows it unless the reviewer typed one. */
  abstract pick(productId: string): Promise<void>;
  abstract changeSealed(updates: Partial<SealedEditState>): void;
  abstract toggleCode(key: string): void;
  /** Settle a catalogue dispute by making the scan one of the products shops are split between. */
  abstract pickCatalog(catalogProductId: string): Promise<void>;
  /** Search the products again for the sealed identity as edited. */
  abstract searchAgain(): Promise<void>;
  /** Ask the source for the shared market estimate; the asking price stays the reviewer's. */
  abstract refreshMarket(): Promise<void>;
  /** Copy the market price into the asking price field, as if typed. */
  abstract useMarketPrice(cents: number): void;
  abstract setCostPerCopy(cost: string): void;
  abstract saveAndReprice(): Promise<void>;
  abstract confirm(): Promise<void>;
  abstract reject(): Promise<void>;
  abstract searchLink(term: string): void;
  abstract pickLink(itemId: string): void;
  abstract clearLink(): void;
  abstract toggleLinkName(): void;
  abstract toggleLinkPrice(): void;
}
